import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient, type QueryObserverResult } from '@tanstack/react-query'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import type { ClaimChatsResponse } from 'tripPlanner/api/types'
import { getTripPlannerVisitorId } from 'tripPlanner/utils/visitorIdentity'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'

interface UseAnonymousChatClaimingArgs {
  isLoggedIn: boolean
  currentChatId: number | undefined
  claimChatsMutation: (args: { visitorId: string }) => Promise<ClaimChatsResponse>
  refetchChatList: () => Promise<QueryObserverResult>
}

export interface AnonymousClaimTransfer {
  chatId: number
  version: number
}

interface UseAnonymousChatClaimingResult {
  waitForClaimCompletion: (signal?: AbortSignal) => Promise<void>
  anonymousClaimTransfer: AnonymousClaimTransfer | undefined
}

export function useAnonymousChatClaiming({
  isLoggedIn,
  currentChatId,
  claimChatsMutation,
  refetchChatList,
}: UseAnonymousChatClaimingArgs): UseAnonymousChatClaimingResult {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const hasClaimedChatsRef = useRef(false)
  const isClaimingChatsRef = useRef(false)
  const pendingClaimRef = useRef<Promise<void> | undefined>(undefined)
  const currentChatIdRef = useRef(currentChatId)
  const [anonymousClaimTransfer, setAnonymousClaimTransfer] = useState<AnonymousClaimTransfer | undefined>(undefined)
  currentChatIdRef.current = currentChatId

  const waitForClaimCompletion = useCallback(async(signal?: AbortSignal): Promise<void> => {
    const pendingClaim = pendingClaimRef.current

    if (!pendingClaim) {
      return
    }

    if (!signal) {
      await pendingClaim
      return
    }

    if (signal.aborted) {
      return
    }

    let removeAbortListener: (() => void) | undefined
    const abortPromise = new Promise<void>((resolve) => {
      const handleAbort = () => resolve()
      signal.addEventListener('abort', handleAbort, { once: true })
      removeAbortListener = () => signal.removeEventListener('abort', handleAbort)
    })

    try {
      await Promise.race([pendingClaim, abortPromise])
    } finally {
      removeAbortListener?.()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (!isLoggedIn) {
      hasClaimedChatsRef.current = false
      isClaimingChatsRef.current = false
      pendingClaimRef.current = undefined
      setAnonymousClaimTransfer(undefined)
      return
    }
    if (hasClaimedChatsRef.current || isClaimingChatsRef.current) {
      return
    }
    const visitorId = getTripPlannerVisitorId()
    if (!visitorId) {
      hasClaimedChatsRef.current = true
      pendingClaimRef.current = undefined
      return
    }

    isClaimingChatsRef.current = true
    let transferredChats = 0
    const claimPromise = claimChatsMutation({ visitorId })
      .then((response) => {
        transferredChats = response.transferredChats
        return refetchChatList()
      })
      .then(async() => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ChatKeys.all }),
          queryClient.invalidateQueries({ queryKey: ChatKeys.itinerary }),
        ])
        const claimedCurrentChatId = currentChatIdRef.current
        if (transferredChats > 0 && claimedCurrentChatId) {
          setAnonymousClaimTransfer((previous) => ({
            chatId: claimedCurrentChatId,
            version: (previous?.version ?? 0) + 1,
          }))
        }
      })
      .catch((error) => {
        console.error('[TripChat] Failed to claim chats for logged in user', error)
        showSnackbar(intl.formatMessage({
          id: 'dxaUBDAJdE579J2WiwME',
          defaultMessage: 'Your previous chat could not be linked to your account. Try signing out and back in.',
          description: 'Snackbar error message when claiming anonymous chat history to a logged-in account fails',
        }), 'critical')
      })
      .finally(() => {
        isClaimingChatsRef.current = false
        hasClaimedChatsRef.current = true
        if (pendingClaimRef.current === claimPromise) {
          pendingClaimRef.current = undefined
        }
      })

    pendingClaimRef.current = claimPromise
  }, [isLoggedIn, claimChatsMutation, refetchChatList, queryClient, intl])

  return {
    waitForClaimCompletion,
    anonymousClaimTransfer,
  }
}
